import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { useApp } from '../lib/app';
import { MODULES } from '../lib/access';
import { APP_NAME, APP_VERSION } from '../lib/version';
import { AlsoHere, OnlineButton } from './Online';
import { initials } from '../lib/presence';

function SiteSwitcher() {
  const { sites, siteId, setSiteId } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = sites.find((s) => s.id === siteId);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  return (
    <div className="site-switch" ref={ref}>
      <button type="button" className="site-btn" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="site-label">CLIENT / SITE</span>
        <span className="site-name">{current ? current.name : sites.length ? 'Choose a site' : 'No sites yet'} ▾</span>
      </button>
      {open && sites.length > 0 ? (
        <ul className="site-menu" role="listbox" aria-label="Client or site">
          {sites.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                role="option"
                aria-selected={s.id === siteId}
                className={s.id === siteId ? 'on' : ''}
                onClick={() => {
                  setSiteId(s.id);
                  setOpen(false);
                }}
              >
                <strong>{s.name}</strong>
                <span>{s.short_code}{s.active ? '' : ' · inactive'}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function UserMenu() {
  const { profile, session, signOut, access } = useApp();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const name = profile?.full_name || session?.user.email || '';
  return (
    <div className="user-menu" ref={ref}>
      <button type="button" className="avatar" aria-label="Account menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {initials(name)}
      </button>
      {open ? (
        <div className="user-pop">
          <strong>{name}</strong>
          <span className="muted small">{session?.user.email}</span>
          <span className="muted small">{access?.super_admin ? 'Superintendent / Super Admin' : access?.roles.map((r) => r.role).join(', ') || 'No role yet'}</span>
          <Link to="/set-password" onClick={() => setOpen(false)}>Change password</Link>
          <button type="button" className="btn btn-secondary" onClick={() => void signOut()}>
            Sign out
          </button>
          <span className="muted small">{APP_NAME} V{APP_VERSION}</span>
        </div>
      ) : null}
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const { can } = useApp();
  const [menu, setMenu] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  useEffect(() => setMenu(false), [loc.pathname]);

  return (
    <div className="shell">
      <nav className={`side ${menu ? 'open' : ''}`} aria-label="Main">
        <Link to="/" className="brand">
          <span className="logo">C3</span>
          <span>
            <strong>{APP_NAME}</strong>
            <small>Construction control</small>
          </span>
        </Link>
        <div className="nav-list">
          {MODULES.map((m) =>
            m.path ? (
              can(m.key) || m.key === 'action_centre' ? (
                <NavLink key={m.key} to={m.path} end={m.path === '/'} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  {m.label}
                </NavLink>
              ) : null
            ) : (
              <span key={m.key} className="nav-item soon" title={`Coming in V${m.version}`}>
                {m.label}
                <em>V{m.version}</em>
              </span>
            ),
          )}
        </div>
        <div className="side-foot">
          {APP_NAME} V{APP_VERSION}
        </div>
      </nav>

      <div className="main">
        <header className="top">
          <button type="button" className="menu-btn" aria-label="Menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
            ☰
          </button>
          <SiteSwitcher />
          <form
            className="search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              const q = new FormData(e.currentTarget).get('q');
              if (q) nav(`/search?q=${encodeURIComponent(String(q))}`);
            }}
          >
            <label>
              <span className="sr">Search</span>
              <input type="search" name="q" placeholder="Search registers — number, topic, building…" />
            </label>
          </form>
          <OnlineButton />
          <UserMenu />
        </header>
        <main className="content">
          <AlsoHere />
          {children}
        </main>
        <footer className="foot">Skyward Industries (SWI) · {APP_NAME} V{APP_VERSION}</footer>
      </div>
    </div>
  );
}
